import React, { useEffect, useMemo, useState } from 'react';
import { Col, FormGroup } from 'reactstrap';
import Select from 'react-select';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import BackupTable from './BackupTable';
import * as backupActions from '../../actions/backupActions';
import * as environmentActions from '../../actions/environmentActions';
import * as storeActions from '../../actions/storeActions';
import StoreOption from './components/StoreOption';

// Migrated from redux-form to plain React state. This "form" has no submit —
// the environment <select> is a UI control that loads cloneable stores, and the
// clone itself happens in BackupTable — so no react-hook-form is needed.
const BackupForm = ({ storeId, toggle }) => {
  const dispatch = useDispatch();
  const environment = useSelector((s) => s.environment);
  const store = useSelector((s) => s.store);
  const backup = useSelector((s) => s.backup);
  const dataCentre = useSelector((s) => s.dataCentre.selected);
  const user = useSelector((s) => s.user);

  const [selectedEnv, setSelectedEnv] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    if (!environment.fetched) dispatch(environmentActions.getAll());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCloneableBackups = (page, size, sort, sortDir) => {
    if (!selectedStore || !selectedEnv) return;
    page = page === undefined ? pagination.page : page;
    size = size || pagination.size;
    setPagination({ ...backup.allCloneable.pagination, page });
    dispatch(backupActions.getAllCloneableByStore(selectedEnv, selectedStore.value, page, size, sort, sortDir));
  };

  // When a store is picked, (re)load its cloneable backups (replaces the old
  // setState(store, callback) pattern).
  useEffect(() => {
    if (selectedStore) loadCloneableBackups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore]);

  const handleEnvironmentChange = (e) => {
    dispatch(storeActions.clear());
    dispatch(backupActions.clear());
    const env = _.find(environment.all, (x) => x.id == e.target.value);
    setSelectedEnv(env);
    dispatch(storeActions.getAllByEnvironment(env, '', dataCentre));
  };

  const handleStoreChange = (selectedOption) => setSelectedStore(selectedOption);

  const debouncedStoreSearch = useMemo(
    () => _.debounce((fuzzyString) => dispatch(storeActions.getAllByEnvironment(selectedEnv, fuzzyString, dataCentre)), 500),
    [selectedEnv, dataCentre, dispatch]
  );

  // react-select's state manager uses a non-undefined return from onInputChange as the
  // controlled inputValue. _.debounce returns the prior call's result, and dispatch(thunk)
  // returns a Promise — so returning it here set inputValue to "[object Promise]", which
  // broke filterOption (stripDiacritics on a non-string) and sent fuzzyString=[object Promise].
  // Fire the search on real typing only, and return nothing so react-select keeps its own input.
  const handleSearchInput = (inputValue, actionMeta) => {
    if (actionMeta.action === 'input-change') {
      debouncedStoreSearch(inputValue);
    }
  };

  // Partners can only clone within their own environment (see backupActions.getAllCloneableByStore).
  const isPartner = user.selected && user.selected.role === 'PARTNER';
  const visibleEnvs = isPartner
    ? environment.all.filter((env) => env.name && env.name.toLowerCase() === process.env.NODE_ENV)
    : environment.all;

  let options = [];
  if (store.allCloneable && store.allCloneable.length > 0) {
    options = store.allCloneable
      .filter((option) => option.storeStatus && option.storeStatus.statusType !== 'DELETED')
      .map((option) => ({ value: option.id, label: option.name, link: option.primaryDomain }));
  }

  return (
    <div>
      <FormGroup row>
        <Col md="2">
          <label htmlFor="environment">Environment</label>
        </Col>
        <Col md="10">
          <select name="environment" className="form-control" onChange={handleEnvironmentChange} required>
            <option />
            {visibleEnvs.map((env) => (
              <option key={env.id} value={env.id}>
                {env.name}
              </option>
            ))}
          </select>
        </Col>
      </FormGroup>
      {options.length > 0 && (
        <div>
          <FormGroup row>
            <Col md="2">
              <label htmlFor="name">Store</label>
            </Col>
            <Col md="10">
              <Select options={options} onChange={handleStoreChange} onInputChange={handleSearchInput} components={{ Option: StoreOption }} />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col md="12">
              <BackupTable
                storeId={storeId}
                all={backup.allCloneable.backups ? backup.allCloneable.backups : []}
                isCloneOnly={true}
                pagination={backup.allCloneable.pagination && backup.allCloneable.pagination !== null ? backup.allCloneable.pagination : null}
                paginate={loadCloneableBackups}
                toggle={toggle}
              />
            </Col>
          </FormGroup>
        </div>
      )}
    </div>
  );
};

export default BackupForm;
